import * as React from "react";
import {
  TextField,
  ReferenceField,
  Show,
  NumberField,
  FunctionField,
  DateField,
  useRecordContext,
  usePermissions,
  Title,
  useDataProvider,
  useNotify,
  useRefresh,
  LinearProgress,
  TopToolbar,
  Button,
  Confirm,
  ArrayField,
  DataTable,
  Form,
  ArrayInput,
  SimpleFormIterator,
  NumberInput,
  FormDataConsumer,
  Labeled,
  PrevNextButtons,
} from "react-admin";
import { ROLE_TYPES } from "../enums/Roles";
import { TabbedShowLayout } from "../common/TabbedShowLayout";
import {
  getPurchaseStatusName,
  isPurchaseMultipleProducts,
  purchaseCanBeRefunded,
  getPurchaseDeliveryStatusName,
  isDeliveryPurchase,
} from "../utils/PurchaseUtils";
import IconReturn from "@mui/icons-material/CurrencyExchange";
import {
  DATE_TIME_FIELD_OPTIONS,
  DATE_TIME_LOCALE,
} from "../utils/DateTimeUtils";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Button as MUIBUtton,
} from "@mui/material";
import { PURCHASE_STATUS } from "../enums/PurchaseStatus";
import { PriceField } from "../common/PriceField";
import { DiscountedPriceField } from "../common/DiscountField";

const PurchaseShowActions = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const record = useRecordContext();
  const [refundDialogOpen, setRefundDialogOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [productsToRefund, setProductsToRefund] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  // Deliver dialog
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [purchaseToDeliver, setPurchaseToDeliver] = React.useState();

  const handleSubmit = (values) => {
    const productsToRefund = values.products
      .map((p) => ({
        product: p.product._id,
        quantity: p.quantityToRefund,
      }))
      .filter((product) => product.quantity > 0);

    if (!productsToRefund?.length) return;

    setProductsToRefund(productsToRefund);

    setRefundDialogOpen(true);
  };

  const onConfirmRefundClick = () => {
    notify("Devolviendo el dinero de la compra...");
    setIsLoading(true);
    dataProvider
      .partialRefundPurchase("purchases", {
        id: record.id,
        products: productsToRefund,
      })
      .then(({ data }) => {
        notify("Devolución del dinero procesada con éxito", {
          type: "success",
        });
        refresh();
      })
      .catch((error) => {
        notify(
          `Ha ocurrido un error devolviendo el dinero de la compra: ${error?.body?.message}`,
          {
            type: "error",
          }
        );
      })
      .finally(() => {
        setIsLoading(false);
        setRefundDialogOpen(false);
        setProductsToRefund([]);
        handleClose();
      });
  };

  const DeliverButton = () => {
    const record = useRecordContext();

    if (record.status !== PURCHASE_STATUS.PAID) return;
    if (isDeliveryPurchase(record)) return;

    const onDeliverPress = (event) => {
      event.stopPropagation();
      setShowConfirm(true);
      setPurchaseToDeliver(record);
    };

    return <Button label="Entregar" onClick={onDeliverPress} />;
  };

  const handleConfirm = async () => {
    setShowConfirm(false);
    if (!purchaseToDeliver) return;

    try {
      const deliveredPurchase = await dataProvider.deliverPurchase(
        "purchases",
        {
          id: purchaseToDeliver._id,
          deliveryCode: purchaseToDeliver.deliveryCode,
        }
      );
      if (deliveredPurchase) {
        notify("¡Pedido entregado con éxito!", {
          type: "success",
        });
      }
    } catch (error) {
      notify(
        `Ha ocurrido un error al entregar el pedido: ${error?.body?.message}`,
        { type: "error" }
      );
    } finally {
      setPurchaseToDeliver(null);
      refresh();
    }
  };

  const handleDialogClose = () => {
    setPurchaseToDeliver(null);
    setShowConfirm(false);
  };

  if (isLoading) return <LinearProgress />;

  return (
    <TopToolbar>
      {purchaseCanBeRefunded(record) && (
        <>
          <Button label="Devolver dinero" onClick={handleOpen}>
            <IconReturn />
          </Button>
          <DeliverButton />
        </>
      )}
      <Confirm
        isOpen={refundDialogOpen}
        loading={false}
        title={`Devolver dinero`}
        content={`¿Estás seguro de devolver los productos seleccionados?`}
        onConfirm={onConfirmRefundClick}
        onClose={() => setRefundDialogOpen(false)}
      />
      <Dialog
        onClose={handleClose}
        open={open}
        fullWidth={true}
        maxWidth={"xs"}
        scroll={"paper"}
      >
        <Form onSubmit={handleSubmit}>
          <DialogTitle>Devolución</DialogTitle>
          <DialogContent>
            <Grid container>
              <ArrayInput source="products" label="">
                <SimpleFormIterator
                  disableReordering
                  disableRemove
                  disableAdd
                  disableClear
                >
                  <FormDataConsumer>
                    {({ scopedFormData }) => (
                      <Box sx={{ flex: "1" }}>
                        <Box sx={{ display: "flex" }}>
                          <Box sx={{ width: "25%" }}>
                            <Labeled label="Disponible">
                              <FunctionField
                                source="quantity"
                                render={(record) => {
                                  return (
                                    (scopedFormData?.quantity || 0) -
                                    (scopedFormData?.refundedQuantity || 0)
                                  );
                                }}
                              />
                            </Labeled>
                          </Box>
                          <Box sx={{ width: "50%" }}>
                            <Labeled label="Producto">
                              <TextField source={"product.name"} />
                            </Labeled>
                          </Box>
                          <Box sx={{ width: "25%" }}>
                            <NumberInput
                              source={"quantityToRefund"}
                              label="Devolver"
                              min={0}
                              max={
                                (scopedFormData.quantity || 0) -
                                (scopedFormData.refundedQuantity || 0)
                              }
                              defaultValue={0}
                            />
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </FormDataConsumer>
                </SimpleFormIterator>
              </ArrayInput>
            </Grid>
          </DialogContent>
          <DialogActions>
            <MUIBUtton type="submit" variant="contained" size="small">
              Confirmar devolución
            </MUIBUtton>
            <MUIBUtton onClick={handleClose} size="small">
              Cerrar
            </MUIBUtton>
          </DialogActions>
        </Form>
      </Dialog>
      <Confirm
        isOpen={showConfirm}
        title={`Entregar pedido`}
        content="¿Estás seguro de entregar este pedido?"
        onConfirm={handleConfirm}
        onClose={handleDialogClose}
      />
      <PrevNextButtons linkType="show" />
    </TopToolbar>
  );
};

const PurchaseTitle = () => {
  const record = useRecordContext();
  const multipleProducts = isPurchaseMultipleProducts(record);
  const purchaseName = multipleProducts
    ? `${record?.productsData?.[0]?.name} y ${record?.productsData?.length} productos más`
    : record?.productData?.name;

  return <Title title={`Pedido "${purchaseName}"`} />;
};

const PurchaseShow = () => {
  const { permissions } = usePermissions();
  const record = useRecordContext();
  return (
    <TabbedShowLayout>
      <TabbedShowLayout.Tab label="Datos básicos">
        {permissions === ROLE_TYPES.ADMIN && (
          <TextField source="id" label="ID" />
        )}

        {permissions === ROLE_TYPES.ADMIN && (
          <ReferenceField
            label="Usuario"
            reference="users"
            source="customer"
            link="show"
          >
            <FunctionField
              source="firstName"
              render={(record) =>
                `${record?.firstName || ""} ${record?.lastName || ""}` || "-"
              }
            />
          </ReferenceField>
        )}
        <ArrayField source="productsData" label="Productos">
          <DataTable
            storeKey="purchase.productsData"
            rowClick={false}
            bulkActionButtons={false}
            sx={{
              "& .column-quantity": {
                width: "100px",
              },
              "& .column-name": {
                maxWidth: "150px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
            }}
          >
            <DataTable.NumberCol source="quantity" label="Cantidad" />
            <DataTable.Col source="product" label="Producto">
              <ReferenceField
                label="Producto"
                reference="products"
                source="product"
                link="show"
              >
                <TextField source="name" />
              </ReferenceField>
            </DataTable.Col>
            <DataTable.Col label={"Pago"}>
              <DiscountedPriceField country={record?.country} label={"Pago"} />
            </DataTable.Col>
          </DataTable>
        </ArrayField>
        {permissions === ROLE_TYPES.ADMIN && (
          <ReferenceField
            label="Restaurante"
            reference="restaurants"
            source="restaurant._id"
            link="show"
          >
            <TextField source="name" />
          </ReferenceField>
        )}

        {permissions === ROLE_TYPES.ADMIN && (
          <PriceField
            source="totals.serviceCostAmount"
            country={record?.country}
            label="Costo de servicio"
          />
        )}

        <FunctionField
          label={"Total Pagado"}
          render={(record) => {
            record.totals.paidAmountWithoutServiceCost =
              record?.totals?.paidAmount - record?.totals?.serviceCostAmount;
            return (
              <PriceField
                record={record}
                source={
                  permissions === ROLE_TYPES.ADMIN
                    ? "totals.paidAmount"
                    : "totals.paidAmountWithoutServiceCost"
                }
                country={record?.country}
              />
            );
          }}
        />

        {permissions === ROLE_TYPES.ADMIN && (
          <PriceField
            source="payment.morfySplitAmount"
            country={record?.country}
            label="Fee Morfy"
          />
        )}
        {permissions === ROLE_TYPES.ADMIN && (
          <PriceField
            source="payment.restaurantSplitAmount"
            country={record?.country}
            label="Fee Restaurante"
          />
        )}

        <FunctionField
          source="status"
          render={(record) => getPurchaseStatusName(record.status)}
          label="Estado"
        />
      </TabbedShowLayout.Tab>
      <TabbedShowLayout.Tab label="Pago">
        <TextField
          source="mercadoPagoData.mercadoPagoPaymentId"
          label="ID Pago (Mercado Pago)"
        />
        {permissions === ROLE_TYPES.ADMIN && [
          <TextField
            key="mercadoPagoData.mercadoPagoPreferenceId"
            source="mercadoPagoData.mercadoPagoPreferenceId"
            label="ID Preferencia"
          />,
          <TextField
            key="mercadoPagoData.mercadoPagoPayerId"
            source="mercadoPagoData.mercadoPagoPayerId"
            label="ID Pagador"
          />,
          <TextField
            key="mercadoPagoData.installments"
            source="mercadoPagoData.installments"
            label="Cuotas"
          />,
          <TextField
            key="mercadoPagoData.mercadoPagoPaymentType"
            source="mercadoPagoData.mercadoPagoPaymentType"
            label="Método de Pago"
          />,
          <TextField
            key="mercadoPagoData.mercadoPagoOrderId"
            source="mercadoPagoData.mercadoPagoOrderId"
            label="ID Orden"
          />,
        ]}
      </TabbedShowLayout.Tab>
      <TabbedShowLayout.Tab label="Compra">
        <DateField
          source="paidDate"
          label="Fecha de Compra"
          showTime={true}
          locales={DATE_TIME_LOCALE}
          options={DATE_TIME_FIELD_OPTIONS}
          emptyText="-"
        />
      </TabbedShowLayout.Tab>
      {isDeliveryPurchase(record) && (
        <TabbedShowLayout.Tab label="Delivery">
          {record?.integrations?.tucan && (
            <TextField source="integrations.tucan.orderCode" label="ID Tucan" />
          )}
          {record?.deliveryData && [
            <TextField
              key="deliveryData.address.fullAddress"
              source="deliveryData.address.fullAddress"
              label="Dirección"
              emptyText="-"
            />,
            <TextField
              key="deliveryData.address.phone"
              source="deliveryData.address.phone"
              label="Teléfono"
              emptyText="-"
            />,
          ]}
          <FunctionField
            label="ETA"
            render={(record) =>
              `${record?.deliveryData.minEstimatedDeliveryMinutes || ""} - ${record?.deliveryData.maxEstimatedDeliveryMinutes || ""} minutos` ||
              "-"
            }
          />
          <FunctionField
            label="Estado"
            render={(record) =>
              getPurchaseDeliveryStatusName(record?.deliveryData?.status)
            }
          />
        </TabbedShowLayout.Tab>
      )}
      <TabbedShowLayout.Tab label="Entrega">
        {permissions === ROLE_TYPES.ADMIN && (
          <TextField source="deliveryCode" label="Código de Entrega" />
        )}
        {record?.deliveredPurchase?.deliveredDate && (
          <DateField
            source="deliveredPurchase.deliveredDate"
            label="Fecha de Entrega"
            showTime={true}
            locales={DATE_TIME_LOCALE}
            options={DATE_TIME_FIELD_OPTIONS}
          />
        )}
        {permissions === ROLE_TYPES.ADMIN &&
          record?.deliveredPurchase?.restaurantUser && (
            <ReferenceField
              label="Entregado por"
              reference="users"
              source="deliveredPurchase.restaurantUser"
              link={false}
            >
              <FunctionField
                source="firstName"
                render={(record) =>
                  `${record?.firstName || ""} ${record?.lastName || ""}` || "-"
                }
              />
            </ReferenceField>
          )}
      </TabbedShowLayout.Tab>

      <TabbedShowLayout.Tab label="Devolución">
        <ArrayField source="products" label="">
          <DataTable
            storeKey="purchase.products"
            rowClick={false}
            bulkActionButtons={false}
            sx={{
              "& .column-quantity": {
                width: "200px",
              },
            }}
          >
            <DataTable.NumberCol source="quantity" label="Cantidad Comprada" />
            <DataTable.Col source="product.name" label="Nombre" />
            <DataTable.Col source="refundedQuantity" label="Cantidad Devuelta">
              <NumberField
                source="refundedQuantity"
                label="Cantidad Devuelta"
                emptyText="0"
              />
            </DataTable.Col>
          </DataTable>
        </ArrayField>
        {record?.refundedPurchase?.refundedDate && (
          <DateField
            source="refundedPurchase.refundedDate"
            label="Fecha de Devolución"
            showTime={true}
            locales={DATE_TIME_LOCALE}
            options={DATE_TIME_FIELD_OPTIONS}
          />
        )}
        {record?.refundedPurchase?.refundedAmount && (
          <PriceField
            source="refundedPurchase.refundedAmount"
            country={record?.country}
            label="Monto Devuelto"
          />
        )}
      </TabbedShowLayout.Tab>

      {permissions === ROLE_TYPES.ADMIN && record?.rejectedPurchase && (
        <TabbedShowLayout.Tab label="Rechazo">
          <DateField
            source="rejectedPurchase.rejectedDate"
            label="Fecha de Rechazo"
            showTime={true}
            locales={DATE_TIME_LOCALE}
            options={DATE_TIME_FIELD_OPTIONS}
          />
          <TextField source="rejectedPurchase.status" label="Estado" />
          <TextField source="rejectedPurchase.statusDetail" label="Detalle" />
        </TabbedShowLayout.Tab>
      )}
    </TabbedShowLayout>
  );
};

const PurchaseShowContainer = () => {
  const { permissions } = usePermissions();

  return (
    <Show
      title={<PurchaseTitle />}
      actions={permissions === ROLE_TYPES.ADMIN && <PurchaseShowActions />}
    >
      <PurchaseShow />
    </Show>
  );
};

export default PurchaseShowContainer;
